import { Button } from '@tunasong/ui-lib'
import { RecordFabButton } from '@tunasong/audio-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Add } from '@tunasong/icons'
import { EntityFilters } from '@tunasong/models'
import { getAcceptedMimeTypes, usePlugins } from '@tunasong/plugin-lib'
import { isFolderContainer } from '@tunasong/schemas'
import { useSearch } from '@tunasong/search'
import { ContentContainer, DndList, DropZone, EntityGrid, Typography, useDeviceInfo, useNavigateToEntity } from '@tunasong/ui-lib'
import { useCurrentUser } from '@tunasong/redux'
import type { CSSProperties, FC, MouseEvent } from 'react'
import { RecentActivity } from './recent-activity.js'

const headerStyle: CSSProperties = {
  margin: 0,
  padding: 8,
  borderRadius: 16,
  background: `linear-gradient(45deg, var(--tuna-palette-background-default) 0%, var(--tuna-palette-background-default)  20%, var(--tuna-palette-primary-main) 50%, var(--tuna-palette-background-default) 80%)`,
}

export interface RootMainProps {}

export const RootMain: FC<RootMainProps> = props => {
  const {} = props
  const { userId } = useCurrentUser()
  const { isMobile } = useDeviceInfo()
  const navigateToEntity = useNavigateToEntity()

  const { results: sharedSearchResults, searching } = useSearch({ queryType: 'shared', query: userId, size: 500 })
  /** Filter out results that has a parentId as well */
  const sharedResultIds = new Set([...sharedSearchResults.map(r => r.id)])

  const { entities: ownedEntities, isLoading: isLoadingEntities } = graphHooks.useEntities({
    root: true,
    filter: EntityFilters.folder,
  })
  const ownedResultIds = new Set([...ownedEntities.map(r => r.id)])

  const sharedResults = sharedSearchResults
    .filter(r => (r.parentId ? !sharedResultIds.has(r.parentId) : true))
    .filter(r => !ownedResultIds.has(r.id))

  const allEntities = [...ownedEntities, ...sharedResults]

  const spaces = allEntities
    // Only show top-level entities
    .filter(e => !Boolean(e.parentId) || e.parentId === 'ROOT')
    .filter(isFolderContainer)
    // Filter out spaces that are public. We may want to surface those in a different way
    // .filter(e => !hasPublicAccess(e.acls))
    .sort((a, b) => (a.name ?? '').localeCompare(b?.name ?? ''))
  const spacesIds = new Set([...spaces.map(r => r.id)])

  const graph = graphHooks.useEntityGraph()

  const addSpace = async (ev: MouseEvent) => {
    ev.preventDefault()

    const space = await graph
      .createEntity({ parent: null, entity: { type: 'folder', name: 'New Space', tags: ['space'] } })
      .unwrap()
    if (space) {
      navigateToEntity(space)
    }
  }
  const busyCreating = graph.createEntityResult.isLoading

  const inbox = ownedEntities.filter(r => !spacesIds.has(r.id))

  const plugins = usePlugins('all')
  const mimeTypes = getAcceptedMimeTypes(plugins)

  return (
    <ContentContainer>
      <Typography style={headerStyle} variant="h2">
        Recent Activity
      </Typography>
      <RecentActivity type="grid" size="medium" />
      <div style={{ position: 'relative' }}>
        <Typography style={{ ...headerStyle, marginBottom: 32, marginTop: 0 }} variant="h2">
          Spaces
        </Typography>
        <Button
          disabled={busyCreating}
          onClick={addSpace}
          startIcon={<Add />}
          style={{ position: 'absolute', right: 0, top: 0, marginTop: 8 }}
          color="primary"
        >
          New Space
        </Button>
      </div>
      <EntityGrid
        size={'large'}
        skeletonItems={9}
        entities={spaces}
        loading={isLoadingEntities || searching}
        onOpen={navigateToEntity}
        placeholder="No spaces found"
      />
      <Typography style={{ ...headerStyle }} variant="h2">
        Inbox
      </Typography>
      <DndList
        size="large"
        isLoading={isLoadingEntities || searching}
        entities={inbox}
        optionsKey={'root'}
        placeholder="No items in Inbox"
      />
      <div style={{ flex: 1 }} />
      <div
        style={{
          position: 'fixed',
          left: '50%',
          bottom: isMobile ? 56 : 16,
          transform: 'translateX(-50%)',
          zIndex: 1050,
        }}
      >
        <RecordFabButton position="relative" color="primary" />
      </div>
      <DropZone parentId={null} accept={mimeTypes} uploadOnClick showClickZone showZone />
    </ContentContainer>
  )
}

export default RootMain
